import React, { Component } from "react";
import {
  Upload,
  Table,
  Tag,
  Alert,
  Typography,
  message,
  Select,
  Modal,
  Progress,
  Tooltip,
} from "antd";
import {
  UploadOutlined,
  FileExcelOutlined,
  DownloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import axios from "axios";
import * as XLSX from "xlsx";
import { ModalItem, ButtonItem } from "./Items";
import { descargarPlantillaInsumos } from "./plantillaInsumosExcel";

const { Text } = Typography;
const { Option } = Select;

const TAMANO_LOTE = 200;

const normalizar = (valor) => String(valor ?? "").trim().toUpperCase();

const parseNumero = (valor) => {
  if (valor === undefined || valor === null || valor === "") return 0;
  if (typeof valor === "number") return valor;
  const limpio = String(valor).replace(/[^0-9.-]/g, "");
  const num = parseFloat(limpio);
  return Number.isNaN(num) ? 0 : num;
};

const texto = (valor) => String(valor ?? "").trim();

// El match de encabezados es por `includes`, asi que los alias mas especificos van
// primero ("PRECIO UNITARIO" antes que "PRECIO") o gana el primero que empate.
// `normalizar` no quita acentos, por eso se listan las dos variantes.
const ALIAS_CLAVE = ["CLAVE", "CÓDIGO", "CODIGO"];
const ALIAS_DESCRIPCION = ["DESCRIPCIÓN", "DESCRIPCION"];
const ALIAS_CANTIDAD = ["CANTIDAD"];
const ALIAS_PRECIO = ["PRECIO UNITARIO", "PRECIO"];
const ALIAS_DESCUENTO = ["DESCUENTO"];
const ALIAS_LEYENDA = ["LEYENDA"];
const ALIAS_OBSERVACIONES = ["OBSERVACIONES", "OBSERVACION"];

const obtenerValorColumna = (fila, alias) => {
  const claves = Object.keys(fila);
  for (const buscado of alias) {
    const clave = claves.find((k) => normalizar(k).includes(buscado));
    if (clave !== undefined) return fila[clave];
  }
  return undefined;
};

// Los Excel que mandan los clientes casi nunca traen los encabezados en la primera
// fila (suele haber logo, titulo o filas en blanco arriba), y la propia plantilla de
// esta app tiene un titulo en la fila 1. Por eso no se usa sheet_to_json directo: se
// busca la primera fila que tenga una columna de Clave reconocible y esa se toma
// como encabezado.
export const filasDesdeHoja = (hoja) => {
  const matriz = XLSX.utils.sheet_to_json(hoja, { header: 1, defval: "" });
  const maximoBusqueda = Math.min(matriz.length, 15);

  let indiceEncabezado = -1;
  for (let i = 0; i < maximoBusqueda; i++) {
    const fila = matriz[i] || [];
    const tieneClave = fila.some((celda) =>
      ALIAS_CLAVE.some((buscado) => normalizar(celda).includes(buscado))
    );
    if (tieneClave) {
      indiceEncabezado = i;
      break;
    }
  }
  if (indiceEncabezado === -1) return [];

  const encabezados = (matriz[indiceEncabezado] || []).map((celda) => String(celda ?? ""));
  return matriz.slice(indiceEncabezado + 1).map((fila) => {
    const objeto = {};
    encabezados.forEach((encabezado, indice) => {
      if (encabezado) objeto[encabezado] = fila[indice] ?? "";
    });
    return objeto;
  });
};

// Parseo de los renglones ya mapeados a objeto {encabezado: valor}. Se exporta para
// poder probarlo sin montar el componente.
export const parsearFilasInsumos = (filasCrudas) =>
  filasCrudas
    .map((fila) => ({
      clave: texto(obtenerValorColumna(fila, ALIAS_CLAVE)),
      descripcion: texto(obtenerValorColumna(fila, ALIAS_DESCRIPCION)),
      cantidad: parseNumero(obtenerValorColumna(fila, ALIAS_CANTIDAD)),
      precioExcel: parseNumero(obtenerValorColumna(fila, ALIAS_PRECIO)),
      descuento: parseNumero(obtenerValorColumna(fila, ALIAS_DESCUENTO)),
      leyenda: texto(obtenerValorColumna(fila, ALIAS_LEYENDA)),
      observaciones: texto(obtenerValorColumna(fila, ALIAS_OBSERVACIONES)),
    }))
    // El filtrado de renglones sin clave va ANTES de armar el payload: el backend
    // responde una entrada por fila recibida y el emparejamiento es por indice.
    .filter((fila) => fila.clave);

class ModalCargarExcelInsumos extends Component {
  state = {
    cargando: false,
    progreso: 0,
    archivoNombre: null,
    filas: [],
    catalogo: {}, // insumoId -> insumo completo
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState({
        cargando: false,
        progreso: 0,
        archivoNombre: null,
        filas: [],
        catalogo: {},
      });
    }
  }

  get nombreDocumento() {
    return this.props.nombreDocumento || "Documento";
  }

  // -------------------------------------------------------------- descargas

  descargarPlantilla = () => {
    descargarPlantillaInsumos(this.nombreDocumento);
  };

  descargarNoEncontrados = () => {
    const noEncontradas = this.state.filas.filter((f) => f.estado === "no_encontrado");
    if (noEncontradas.length === 0) return;
    const hoja = XLSX.utils.json_to_sheet(
      noEncontradas.map((f) => ({
        Clave: f.clave,
        Descripcion: f.descripcion,
        Cantidad: f.cantidad,
        "Precio Unitario": f.precioExcel,
        Motivo: "No se encontro un insumo de venta con esa clave",
      }))
    );
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "No encontrados");
    XLSX.writeFile(
      libro,
      `Insumos_No_Encontrados_${this.nombreDocumento.replace(/\s+/g, "_")}.xlsx`
    );
  };

  // ----------------------------------------------------------------- lectura

  onSubirArchivo = (info) => {
    const file = info.file;
    if (!/\.xlsx?$/i.test(file.name || "")) {
      message.error("El archivo debe ser un Excel (.xlsx/.xls)");
      return;
    }
    this.setState({
      cargando: true,
      progreso: 0,
      archivoNombre: file.name,
      filas: [],
      catalogo: {},
    });

    const reader = new FileReader();
    reader.onloadend = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const libro = XLSX.read(data, { type: "array" });
        const primeraHoja = libro.Sheets[libro.SheetNames[0]];
        this.procesarFilas(filasDesdeHoja(primeraHoja));
      } catch (err) {
        console.error(err);
        message.error("No se pudo leer el archivo. Verifica que sea un Excel valido.");
        this.setState({ cargando: false });
      }
    };
    reader.readAsArrayBuffer(file.originFileObj || file);
  };

  procesarFilas = async (filasCrudas) => {
    const filasParseadas = parsearFilasInsumos(filasCrudas);

    if (filasParseadas.length === 0) {
      message.error("No se encontraron renglones con una columna de Clave reconocible.");
      this.setState({ cargando: false });
      return;
    }

    const { empresaId, headersToken, clienteId, precioId } = this.props;
    const resultados = [];
    const catalogo = {};

    try {
      // En tandas para no armar un payload gigante de insumos completos, y de paso
      // darle barra de progreso al usuario.
      for (let inicio = 0; inicio < filasParseadas.length; inicio += TAMANO_LOTE) {
        const lote = filasParseadas.slice(inicio, inicio + TAMANO_LOTE);
        const res = await axios.post(
          "insumos/resolverClavesInsumosVentaCompletoMasivo",
          {
            empresaId,
            clienteId: clienteId || null,
            precioId: precioId || null,
            filas: lote.map((fila) => ({ clave: fila.clave, descripcion: fila.descripcion })),
          },
          { headers: headersToken }
        );

        if (res.data?.success !== 2) {
          message.error(res.data?.message || "No se pudieron resolver las claves del Excel");
          this.setState({ cargando: false });
          return;
        }

        (res.data.insumos || []).forEach((insumo) => {
          catalogo[String(insumo._id)] = insumo;
        });
        resultados.push(...(res.data.resultados || []));
        this.setState({
          progreso: Math.round(((inicio + lote.length) / filasParseadas.length) * 100),
        });
      }
    } catch (err) {
      console.error(err);
      message.error("Error de conexion al resolver las claves del Excel");
      this.setState({ cargando: false });
      return;
    }

    const filas = filasParseadas.map((fila, indice) => {
      const resultado = resultados[indice];
      if (!resultado || resultado.estado === "NO_ENCONTRADO") {
        return { ...fila, estado: "no_encontrado", insumo: null, candidatos: [] };
      }
      if (resultado.estado === "AMBIGUO") {
        return { ...fila, estado: "ambiguo", insumo: null, candidatos: resultado.candidatos || [] };
      }
      return {
        ...fila,
        estado: "encontrado",
        insumo: catalogo[String(resultado.insumoId)] || null,
        candidatos: [],
        precioSugerido: resultado.precioSugerido,
        desempatadoPorDescripcion: !!resultado.desempatadoPorDescripcion,
      };
    });

    this.setState({ filas, catalogo, cargando: false, progreso: 100 });
  };

  elegirCandidato = (indice, insumoId) => {
    this.setState((prev) => {
      const insumo = prev.catalogo[String(insumoId)];
      if (!insumo) return null;
      const filas = [...prev.filas];
      filas[indice] = {
        ...filas[indice],
        estado: "encontrado",
        insumo,
        candidatos: [],
        precioSugerido: insumo.precioClienteAlternativo,
      };
      return { filas };
    });
  };

  // --------------------------------------------------------------- confirmar

  filasValidas = () => this.state.filas.filter((f) => f.estado === "encontrado" && f.insumo);

  confirmar = (modo) => {
    const validas = this.filasValidas();
    if (validas.length === 0) {
      message.error("No hay renglones validos para cargar");
      return;
    }

    const cargar = () =>
      this.props.onConfirmar(
        validas.map((f) => ({
          insumo: f.insumo,
          cantidad: f.cantidad,
          // null = el Excel no traia precio; la pagina lo resuelve con la lista de
          // precios, igual que en el alta manual.
          precioExcel: f.precioExcel > 0 ? f.precioExcel : null,
          descuento: f.descuento || 0,
          leyenda: f.leyenda || undefined,
          observaciones: f.observaciones || undefined,
        })),
        modo
      );

    if (modo === "reemplazar") {
      Modal.confirm({
        title: "Reemplazar los insumos capturados",
        icon: <WarningOutlined style={{ color: "#faad14" }} />,
        content: `Se van a borrar los ${
          this.props.cantidadInsumosCapturados || 0
        } renglon(es) capturados y quedaran solo los ${validas.length} del Excel.`,
        okText: "Reemplazar",
        cancelText: "Cancelar",
        onOk: cargar,
      });
      return;
    }
    cargar();
  };

  // ------------------------------------------------------------------ render

  columnasTabla = () => [
    { title: "Clave", dataIndex: "clave", width: 110 },
    {
      title: "Insumo",
      render: (_, fila) =>
        fila.insumo ? (
          <span>
            {fila.insumo.descripcion}
            {fila.desempatadoPorDescripcion && (
              <Tooltip title="Habia varios insumos con esta clave; se eligio por la descripcion del Excel">
                <Tag color="blue" style={{ marginLeft: 6 }}>
                  por descripcion
                </Tag>
              </Tooltip>
            )}
          </span>
        ) : (
          <Text type="secondary">{fila.descripcion || "—"}</Text>
        ),
    },
    { title: "Cant.", dataIndex: "cantidad", width: 70 },
    {
      title: "P. Unitario",
      width: 120,
      render: (_, fila) => {
        const precio = fila.precioExcel > 0 ? fila.precioExcel : fila.precioSugerido;
        if (precio === null || precio === undefined) return <Text type="secondary">Sin precio</Text>;
        return `$${Number(precio).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;
      },
    },
    {
      title: "Estado",
      width: 300,
      render: (_, fila, indice) => {
        if (fila.estado === "no_encontrado") {
          return <Tag color="red">No se encontro esa clave</Tag>;
        }
        if (fila.estado === "ambiguo") {
          return (
            <Select
              size="small"
              style={{ width: "100%" }}
              placeholder={`${fila.candidatos.length} insumos con esa clave, elige uno`}
              onChange={(valor) => this.elegirCandidato(indice, valor)}
            >
              {fila.candidatos.map((candidato) => (
                <Option key={String(candidato._id)} value={String(candidato._id)}>
                  {candidato.stringBuscar || candidato.descripcion}
                </Option>
              ))}
            </Select>
          );
        }

        const avisos = [];
        // La leyenda SUSTITUYE la descripcion en el PDF/CFDI, pero solo si el insumo
        // trae prendido leyendaVenta. Se carga igual, pero hay que avisarlo o el dato
        // se queda invisible sin explicacion.
        if (fila.leyenda && !fila.insumo?.leyendaVenta) {
          avisos.push(
            <Tooltip
              key="leyenda"
              title="La leyenda se guardara, pero no se imprimira: este insumo no tiene activada la opcion 'Leyenda en Venta' en su catalogo."
            >
              <Tag color="gold">Leyenda no se imprimira</Tag>
            </Tooltip>
          );
        }
        if (
          fila.precioExcel <= 0 &&
          (fila.precioSugerido === null || fila.precioSugerido === undefined)
        ) {
          avisos.push(
            <Tooltip
              key="precio"
              title="El Excel no trae precio y el insumo no tiene precio en la lista seleccionada. Entrara en 0."
            >
              <Tag color="gold">Sin precio</Tag>
            </Tooltip>
          );
        }
        return (
          <span>
            <Tag color="green">Encontrado</Tag>
            {avisos}
          </span>
        );
      },
    },
  ];

  render() {
    const { visible, onCancel, hayInsumosCapturados, permitirReemplazar } = this.props;
    const { cargando, progreso, archivoNombre, filas } = this.state;

    const validas = this.filasValidas().length;
    const ambiguas = filas.filter((f) => f.estado === "ambiguo").length;
    const noEncontradas = filas.filter((f) => f.estado === "no_encontrado").length;

    return (
      <ModalItem
        title={
          <span>
            <FileExcelOutlined /> Cargar Insumos desde Excel
          </span>
        }
        visible={visible}
        onCancel={onCancel}
        width={1000}
        spinning={cargando}
      >
        <div style={{ padding: "0 15px 15px 15px" }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 15 }}
            message="Columnas: Clave, Descripcion, Cantidad, Precio Unitario, Descuento (%), Leyenda y Observaciones"
            description="Solo la Clave es obligatoria. La Descripcion sirve para desempatar cuando dos insumos comparten clave. Si no pones Precio Unitario se usa el de la lista de precios del documento. Los encabezados pueden estar en cualquier fila."
          />

          {hayInsumosCapturados && !permitirReemplazar && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 15 }}
              message="Solo se puede agregar al final"
              description="Ya hay renglones con un pedido asignado. Reemplazarlos perderia esos vinculos, asi que los renglones del Excel se agregaran despues de los que ya hay."
            />
          )}

          <ButtonItem
            icon={<DownloadOutlined />}
            onClick={this.descargarPlantilla}
            style={{ marginRight: 10 }}
          >
            Descargar plantilla
          </ButtonItem>

          <Upload showUploadList={false} customRequest={this.onSubirArchivo} accept=".xlsx,.xls">
            <ButtonItem icon={<UploadOutlined />}>Subir Excel</ButtonItem>
          </Upload>

          {archivoNombre && (
            <Text type="secondary" style={{ marginLeft: 10 }}>
              {archivoNombre}
            </Text>
          )}

          {cargando && progreso > 0 && (
            <Progress percent={progreso} size="small" style={{ marginTop: 12 }} />
          )}

          {filas.length > 0 && (
            <>
              <div
                style={{ display: "flex", alignItems: "center", gap: 10, margin: "15px 0 10px" }}
              >
                <Text>
                  {validas} renglon(es) listo(s)
                  {ambiguas > 0 ? `, ${ambiguas} por definir` : ""}
                  {noEncontradas > 0 ? `, ${noEncontradas} no encontrado(s)` : ""}
                </Text>
                {noEncontradas > 0 && (
                  <ButtonItem
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={this.descargarNoEncontrados}
                  >
                    Descargar no encontrados
                  </ButtonItem>
                )}
              </div>
              <Table
                size="small"
                rowKey={(fila, indice) => indice}
                columns={this.columnasTabla()}
                dataSource={filas}
                pagination={{ pageSize: 8 }}
              />
            </>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 15, gap: 10 }}>
            <ButtonItem onClick={onCancel} rojo>
              Cancelar
            </ButtonItem>
            {hayInsumosCapturados && permitirReemplazar && (
              <ButtonItem disabled={validas === 0} onClick={() => this.confirmar("reemplazar")}>
                Reemplazar todo ({validas})
              </ButtonItem>
            )}
            <ButtonItem
              type="primary"
              disabled={validas === 0}
              onClick={() => this.confirmar("agregar")}
            >
              {hayInsumosCapturados ? `Agregar al final (${validas})` : `Cargar (${validas})`}
            </ButtonItem>
          </div>
        </div>
      </ModalItem>
    );
  }
}

export default ModalCargarExcelInsumos;
